/**
 * vskiConfig.ts — fetch + mutate the backend's VSKI platform storage config.
 *
 * Powers the Connectivity wizard's storage/login steps and
 * the Settings → Storage tab. The backend is the credential broker: it probes
 * the platform, mints an admin API key from a one-time email/password login
 * (the password is never persisted — only the minted key, encrypted in the
 * keystore), or accepts a manually pasted key. The status response NEVER
 * contains key material — `source` says where the key lives ('env' is
 * operator-managed and read-only in the UI; 'stored' is keystore-managed and
 * clearable).
 *
 * Same conventions as src/chat/aiConfig.ts: one fetch hook with a nonce
 * `reload()`, mutation helpers that throw Error(detail) for inline display.
 */
import { useCallback, useEffect, useState } from 'react';

export interface VskiPlatformStatus {
  reachable: boolean;
  hasAdmins: boolean;
  /** Present when a key is configured: did it authenticate with admin rights? */
  authOk: boolean | null;
  detail?: string;
}

export interface VskiStatus {
  /** True when url + admin key are both resolved (env or stored). */
  configured: boolean;
  url: string;
  source: 'env' | 'stored' | 'none';
  sdk: boolean;
  keystore: boolean;
  platform: VskiPlatformStatus;
  /** Browser-reachable /installer link (null when not derivable). */
  installerUrl: string | null;
  /** Discovered conventional URL when nothing is configured (hint only). */
  suggestedUrl: string | null;
}

interface VskiStatusState {
  status: VskiStatus | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

function authHeaders(token: string | null): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

async function errorText(r: Response): Promise<string> {
  const fallback = `request failed (${r.status})`;
  try {
    const j = (await r.json()) as { detail?: unknown };
    if (typeof j?.detail === 'string') return j.detail;
    if (j?.detail) return JSON.stringify(j.detail);
  } catch {
    /* ignore */
  }
  return fallback;
}

export function useVskiStatus(
  backendOrigin: string | null,
  token: string | null,
  /** Optional user-entered platform URL to probe instead of the configured
   *  one (the setup form's custom-address path; null = backend decides). */
  probeUrl: string | null = null,
): VskiStatusState {
  const [status, setStatus] = useState<VskiStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!backendOrigin) {
      setStatus(null);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const qs = probeUrl ? `?url=${encodeURIComponent(probeUrl)}` : '';
    fetch(`${backendOrigin}/api/vski/status${qs}`, { headers: authHeaders(token) })
      .then(async (r) => {
        if (!r.ok) throw new Error(await errorText(r));
        return (await r.json()) as VskiStatus;
      })
      .then((s) => {
        if (!cancelled) {
          setStatus(s);
          setLoading(false);
        }
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setStatus(null);
          setLoading(false);
          setError(e.message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [backendOrigin, token, probeUrl, nonce]);

  return { status, loading, error, reload };
}

// ─── Mutations (call `reload()` afterward — one authoritative code path) ────

export interface VskiMutationResult {
  ok: boolean;
  detail?: string;
  url?: string;
}

/**
 * Login with platform superuser credentials; the backend mints an admin API
 * key and stores it encrypted. Returns {ok:false, detail} on platform errors
 * (wrong password, unreachable, …) so the caller shows the message inline.
 */
export async function connectVskiPlatform(
  backendOrigin: string,
  token: string | null,
  body: { email: string; password: string; url?: string },
): Promise<VskiMutationResult> {
  const r = await fetch(`${backendOrigin}/api/vski/connect`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(await errorText(r));
  return (await r.json()) as VskiMutationResult;
}

/** Store a manually pasted admin API key (validated server-side first). */
export async function saveVskiKey(
  backendOrigin: string,
  token: string | null,
  body: { apiKey: string; url?: string },
): Promise<VskiMutationResult> {
  const r = await fetch(`${backendOrigin}/api/vski/key`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(await errorText(r));
  return (await r.json()) as VskiMutationResult;
}

/** Remove the stored credential (env-managed config is refused server-side). */
export async function clearVskiPlatform(
  backendOrigin: string,
  token: string | null,
): Promise<VskiMutationResult> {
  const r = await fetch(`${backendOrigin}/api/vski`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!r.ok) throw new Error(await errorText(r));
  return (await r.json()) as VskiMutationResult;
}
